import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import videojs from 'video.js';
import { useInView } from 'react-intersection-observer';

import cx from '@strava/ui/clsx';

import { useFeedContext } from '../../../FeedContext';
import { useFeedEntityContext } from '../../../FeedEntityContext';

import styles from './VideoItem.scss';

const Poc = React.lazy(() => import('components/Poc'));
const Controls = React.lazy(() => import('./Controls'));

function VideoItem({
  media,
  className,
  onClick,
  onReady,
  playOnHover,
  isAutoPlayItem
}) {
  const {
    entityState: { isActivityVideoMuted }
  } = useFeedContext();

  const {
    feedEntityState: { isAutoplayEnabled }
  } = useFeedEntityContext();
  const [video, setVideo] = useState();
  const hoverTimerRef = useRef();

  function play() {
    clearTimeout(hoverTimerRef.current);

    hoverTimerRef.current = setTimeout(() => {
      // Pause all video that are currently playing
      videojs
        .getAllPlayers()
        .filter((p) => !p.paused() && p.id() !== video.id())
        .forEach((p) => p.pause());

      if (video && video.paused()) {
        video.play();
      }
    }, 500);
  }

  function pause() {
    clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = null;

    if (video && !video.paused()) {
      video.pause();
    }
  }

  const preload = (() => {
    if (isAutoPlayItem) {
      return 'auto';
    }
    if (isAutoplayEnabled) {
      return 'metadata';
    }
    return 'none';
  })();

  //
  // Handle resitting video to initial state when element exist the screen
  //
  const [inViewRef, inView] = useInView({ threshold: 0 });

  useEffect(() => {
    if (video && video.currentTime() !== 0 && !inView) {
      // Reset any played videos that are exiting the screen
      video.pause();
      video.currentTime(0);
      // Show poster image
      video.hasStarted(false);
      video.posterImage.show();
    }
  }, [video, inView]);

  return (
    <div
      ref={inViewRef}
      className={cx(styles.base, className)}
      onClick={() => {
        pause();
        onClick();
      }}
      onKeyPress={() => {
        pause();
        onClick();
      }}
      onMouseEnter={() => {
        if (!isAutoplayEnabled) return;
        if (!playOnHover) return;
        play();
      }}
      onMouseLeave={() => {
        if (!isAutoplayEnabled) return;
        if (!playOnHover) return;
        if (isAutoPlayItem) return;
        pause();
      }}
      role="button"
      tabIndex="0"
    >
      <React.Suspense fallback={<div />}>
        <Controls
          duration={media.duration}
          video={video}
          videoId={media.photo_id}
          isAutoplayEnabled={isAutoplayEnabled}
        />
        <Poc
          media={media}
          onReady={(vid) => {
            setVideo(vid);
            onReady(vid);
          }}
          options={{
            controls: false,
            loop: true,
            muted: isActivityVideoMuted,
            preload
          }}
          className={styles.video}
        />
      </React.Suspense>
    </div>
  );
}

VideoItem.defaultProps = {
  playOnHover: true,
  className: null,
  isAutoPlayItem: false,
  onClick: () => {},
  onReady: () => {}
};

VideoItem.propTypes = {
  media: PropTypes.shape({
    duration: PropTypes.number,
    photo_id: PropTypes.string,
    caption_escaped: PropTypes.string,
    owner_id: PropTypes.string
  }).isRequired,
  className: PropTypes.string,
  onClick: PropTypes.func,
  onReady: PropTypes.func,
  playOnHover: PropTypes.bool,
  isAutoPlayItem: PropTypes.bool
};

export default VideoItem;
